// Production engine (spec §101): an auditable ledger of credited production, distinct from the
// current portfolio and from collected cash.
//
// Rules (configurable in settings key `production`, defaults documented here and in docs):
//  - New business is credited once, when a policy first becomes active/renewed; amount = its
//    annualised premium at the start date (basis 'annualised'), or the one-off amount for single
//    premiums (basis 'single' — never annualised into recurring production).
//  - A replacement (policy.replacesPolicyId) is credited as 'replacement', not as new business.
//  - An increase/decrease credits only the signed delta (never the new full premium).
//  - Credit date: the effective date; if that lies before the current month (backdated), the credit
//    goes to today and the event says so (closed periods are never rewritten).
//  - A cancellation within `cancellationWindowDays` of the start reverses the new-business credit
//    with a linked 'cancellation' event; later lapses are retention events, not negative production.
//  - A reinstatement (lapsed/cancelled → active) is credited as 'reactivation'.
//  - Every event has an idempotency key; generating twice never credits twice.
//  - Context tags (cross_sale, upsell, campaign) describe the sale without creating extra totals.
import type { Changeset, Put, WorkspaceView } from './changeset.ts';
import { DomainError, created, put, type Ctx } from './cmd.ts';
import { addDays, addMonths, diffDays, startOfWeek, type Day } from './dates.ts';
import { fromMinor, isRecurring, toMinor } from './money.ts';
import { annualNow, currencyOf, policyAgeDays, policyCategory, premiumAt, LIFE_CATEGORIES } from './policy-engine.ts';
import type { Policy, PolicyAdjustment, ProductionEvent, ProductionType } from './schema.ts';
import { getSetting } from './services.ts';

export interface ProductionSettings { cancellationWindowDays: number; backdated: 'credit_today' | 'credit_effective'; fiscalYearStartMonth: number; weekStartsOn: number }
export const DEFAULT_PRODUCTION: ProductionSettings = { cancellationWindowDays: 365, backdated: 'credit_today', fiscalYearStartMonth: 1, weekStartsOn: 1 };
export const productionSettings = (v: WorkspaceView): ProductionSettings => ({ ...DEFAULT_PRODUCTION, ...getSetting<Partial<ProductionSettings>>(v, 'production', {}) });

const isLive = (s: Policy['status']) => s === 'active' || s === 'renewed';
const monthStart = (d: Day) => `${d.slice(0, 7)}-01`;
function creditDate(effective: Day, ctx: Ctx, cfg: ProductionSettings): { date: Day; backdated: boolean } {
  if (cfg.backdated === 'credit_today' && effective < monthStart(ctx.today)) return { date: ctx.today, backdated: true };
  return { date: effective, backdated: false };
}
function eventRow(ctx: Ctx, p: Policy, fields: { type: ProductionType; date: Day; effectiveDate?: Day | null; annualDelta: number; basis: string; key: string; contexts?: string[]; sourceTable?: string; sourceId?: string; reversesId?: string | null; campaignId?: string | null; note?: string | null; backdated?: boolean }): ProductionEvent {
  return created<'production_events'>(ctx, {
    personId: p.personId, policyId: p.id, type: fields.type, date: fields.date, effectiveDate: fields.effectiveDate ?? fields.date,
    annualDelta: fields.annualDelta, basis: fields.basis, currency: currencyOf(p, ctx.region), branch: p.branch, category: policyCategory(p),
    contexts: fields.contexts?.length ? fields.contexts : null, sourceTable: fields.sourceTable ?? 'policies', sourceId: fields.sourceId ?? p.id,
    key: fields.key, reversesId: fields.reversesId ?? null, campaignId: fields.campaignId ?? null, note: fields.note ?? null,
    data: fields.backdated ? { backdated: true } : null,
  });
}
const hasKey = (v: WorkspaceView, key: string) => v.where('production_events', 'key', key).length > 0;

/** Sales context at the time of a new policy: cross-sale when the client already held another live policy. */
function contextsFor(v: WorkspaceView, p: Policy, on: Day): string[] {
  const out: string[] = [];
  const others = v.where('policies', 'personId', p.personId).filter((x) => x.id !== p.id && isLive(x.status) && (x.startDate ?? x.issueDate ?? '0000') <= on);
  if (others.length) out.push(others.some((x) => policyCategory(x) === policyCategory(p)) ? 'upsell' : 'cross_sale');
  const campaign = (p.data as { campaignId?: string } | undefined)?.campaignId;
  if (campaign) out.push('campaign');
  return out;
}

/** Events a policy change should create (idempotent: returns [] when already credited). */
export function policyProductionPuts(v: WorkspaceView, before: Policy | undefined, after: Policy, ctx: Ctx): Put[] {
  const cfg = productionSettings(v);
  const puts: Put[] = [];
  const nbKey = `nb:${after.id}`;
  const becameLive = isLive(after.status) && (!before || !isLive(before.status));
  if (becameLive && (!before || before.status === 'offer' || before.status === 'pending') && !hasKey(v, nbKey)) {
    const eff = after.startDate ?? after.issueDate ?? ctx.today;
    const { date, backdated } = creditDate(eff, ctx, cfg);
    const recurring = isRecurring(after.paymentFrequency) || !after.paymentFrequency;
    const amount = premiumAt(after, v.where('policy_adjustments', 'policyId', after.id).filter((a) => a.date && a.date <= eff && !a.reversesId), eff);
    puts.push(put('production_events', eventRow(ctx, after, {
      type: after.replacesPolicyId ? 'replacement' : 'new_business', date, effectiveDate: eff, annualDelta: amount, basis: recurring ? 'annualised' : 'single',
      key: nbKey, contexts: contextsFor(v, after, eff), campaignId: (after.data as { campaignId?: string } | undefined)?.campaignId ?? null, backdated,
    })));
  }
  if (before && isLive(after.status) && (before.status === 'lapsed' || before.status === 'cancelled')) {
    const key = `react:${after.id}:${ctx.today}`;
    if (!hasKey(v, key)) puts.push(put('production_events', eventRow(ctx, after, { type: 'reactivation', date: ctx.today, annualDelta: annualNow(v, after, ctx.today), basis: 'annualised', key })));
  }
  if (before && isLive(before.status) && after.status === 'cancelled') {
    const nb = v.where('production_events', 'key', nbKey)[0];
    const key = `cxl:${after.id}`;
    if (nb && !hasKey(v, key) && policyAgeDays(after, ctx.today) <= cfg.cancellationWindowDays && !v.rows('production_events').some((e) => e.reversesId === nb.id)) {
      puts.push(put('production_events', eventRow(ctx, after, { type: 'cancellation', date: ctx.today, annualDelta: -nb.annualDelta, basis: nb.basis, key, reversesId: nb.id, note: 'within_cancellation_window' })));
    }
  }
  return puts;
}

/** Event for a premium adjustment: the delta only, once. */
export function adjustmentProductionPuts(v: WorkspaceView, adj: PolicyAdjustment, ctx: Ctx): Put[] {
  const p = v.get('policies', adj.policyId);
  if (!p || !isLive(p.status)) return [];
  const key = `adj:${adj.id}`;
  if (hasKey(v, key) || !adj.annualAmount) return [];
  const eff = adj.date ?? ctx.today;
  const { date, backdated } = creditDate(eff, ctx, productionSettings(v));
  const recurring = isRecurring(p.paymentFrequency) || !p.paymentFrequency;
  return [put('production_events', eventRow(ctx, p, {
    type: adj.annualAmount > 0 ? 'increase' : 'decrease', date, effectiveDate: eff, annualDelta: adj.annualAmount, basis: recurring ? 'annualised' : 'single',
    key, sourceTable: 'policy_adjustments', sourceId: adj.id, backdated, campaignId: (adj.data as { campaignId?: string } | undefined)?.campaignId ?? null,
  }))];
}
/** Reversal of an adjustment reverses its production event (linked, never deleted). */
export function reversalProductionPuts(v: WorkspaceView, rev: PolicyAdjustment, original: PolicyAdjustment, ctx: Ctx): Put[] {
  const p = v.get('policies', rev.policyId);
  const orig = v.where('production_events', 'key', `adj:${original.id}`)[0];
  const key = `rev:${original.id}`;
  if (!p || !orig || hasKey(v, key)) return [];
  return [put('production_events', eventRow(ctx, p, { type: 'reversal', date: ctx.today, effectiveDate: orig.effectiveDate, annualDelta: -orig.annualDelta, basis: orig.basis, key, sourceTable: 'policy_adjustments', sourceId: rev.id, reversesId: orig.id }))];
}

/**
 * Build the ledger from an existing portfolio (legacy import, first run): one new-business event per
 * live policy at its start date and one event per dated adjustment. Idempotent; returns a preview count.
 */
export function backfillProduction(v: WorkspaceView, ctx: Ctx): Changeset {
  const puts: Put[] = [];
  const keys = new Set(v.rows('production_events').map((e) => e.key));
  const add = (row: ProductionEvent) => { if (!keys.has(row.key)) { keys.add(row.key); puts.push(put('production_events', row)); } };
  for (const p of v.rows('policies')) {
    if (!isLive(p.status) && p.status !== 'lapsed' && p.status !== 'cancelled' && p.status !== 'matured') continue;
    const eff = p.startDate ?? p.issueDate;
    if (!eff) continue;
    const recurring = isRecurring(p.paymentFrequency) || !p.paymentFrequency;
    add(eventRow(ctx, p, { type: p.replacesPolicyId ? 'replacement' : 'new_business', date: eff, effectiveDate: eff, annualDelta: p.annualPremium || 0, basis: recurring ? 'annualised' : 'single', key: `nb:${p.id}`, note: 'backfill' }));
    for (const a of v.where('policy_adjustments', 'policyId', p.id)) {
      if (!a.annualAmount || a.reversesId || !a.date || a.type === 'renewal' || a.type === 'reversal') continue;
      add(eventRow(ctx, p, { type: a.annualAmount > 0 ? 'increase' : 'decrease', date: a.date, effectiveDate: a.date, annualDelta: a.annualAmount, basis: recurring ? 'annualised' : 'single', key: `adj:${a.id}`, sourceTable: 'policy_adjustments', sourceId: a.id, note: 'backfill' }));
    }
  }
  return { action: 'production.backfill', entity: 'production_events', summary: String(puts.length), puts, deletes: [] };
}

// --- reports -------------------------------------------------------------------------------
export type PeriodKind = 'today' | 'week' | 'month' | 'quarter' | 'year';
export interface Range { from: Day; to: Day }
export function periodRange(kind: PeriodKind, today: Day, cfg: Pick<ProductionSettings, 'fiscalYearStartMonth' | 'weekStartsOn'> = DEFAULT_PRODUCTION, offset = 0): Range {
  switch (kind) {
    case 'today': { const d = addDays(today, offset); return { from: d, to: d }; }
    case 'week': { const f = addDays(startOfWeek(today, cfg.weekStartsOn), offset * 7); return { from: f, to: addDays(f, 6) }; }
    case 'month': { const f = addMonths(monthStart(today), offset); return { from: f, to: addDays(addMonths(f, 1), -1) }; }
    case 'quarter': { const m = Number(today.slice(5, 7)); const q0 = `${today.slice(0, 4)}-${String(Math.floor((m - 1) / 3) * 3 + 1).padStart(2, '0')}-01`; const f = addMonths(q0, offset * 3); return { from: f, to: addDays(addMonths(f, 3), -1) }; }
    case 'year': {
      const sm = cfg.fiscalYearStartMonth;
      const y = Number(today.slice(0, 4)) - (Number(today.slice(5, 7)) < sm ? 1 : 0);
      const f = addMonths(`${y}-${String(sm).padStart(2, '0')}-01`, offset * 12);
      return { from: f, to: addDays(addMonths(f, 12), -1) };
    }
  }
}
/**
 * Comparison with the previous corresponding period, to date: the current period so far is compared
 * with the same number of days of the previous period (e.g. 1–15 Sep vs 1–15 Aug).
 */
export function comparableRange(kind: PeriodKind, today: Day, cfg: Pick<ProductionSettings, 'fiscalYearStartMonth' | 'weekStartsOn'> = DEFAULT_PRODUCTION): { current: Range; previous: Range } {
  const cur = periodRange(kind, today, cfg);
  const prev = periodRange(kind, today, cfg, -1);
  const elapsed = diffDays(cur.from, today);
  const prevTo = addDays(prev.from, elapsed) > prev.to ? prev.to : addDays(prev.from, elapsed);
  return { current: { from: cur.from, to: today < cur.to ? today : cur.to }, previous: { from: prev.from, to: prevTo } };
}

export interface ProductionTotals { byCurrency: Record<string, number>; byType: Partial<Record<ProductionType, Record<string, number>>>; life: Record<string, number>; general: Record<string, number>; count: number; events: ProductionEvent[]; contexts: Record<string, number> }
function add(map: Record<string, number>, cur: string, amount: number) { map[cur] = fromMinor(toMinor(map[cur] ?? 0, cur) + toMinor(amount, cur), cur); }
export function totalsOf(events: readonly ProductionEvent[], range: Range, filter?: (e: ProductionEvent) => boolean): ProductionTotals {
  const out: ProductionTotals = { byCurrency: {}, byType: {}, life: {}, general: {}, count: 0, events: [], contexts: {} };
  for (const e of events) {
    if (e.date < range.from || e.date > range.to || (filter && !filter(e))) continue;
    out.events.push(e);
    out.count++;
    add(out.byCurrency, e.currency, e.annualDelta);
    add((out.byType[e.type] ??= {}), e.currency, e.annualDelta);
    const life = e.branch ? e.branch === 'life' : LIFE_CATEGORIES.includes(e.category as never);
    add(life ? out.life : out.general, e.currency, e.annualDelta);
    for (const c of e.contexts ?? []) out.contexts[c] = (out.contexts[c] ?? 0) + 1;
  }
  out.events.sort((a, b) => b.date.localeCompare(a.date));
  return out;
}
/** Percentage change, or null when the previous value is zero or missing (never "∞%" or NaN). */
export function changePct(current: number, previous: number | null | undefined): number | null {
  if (!previous) return null;
  return (current - previous) / Math.abs(previous);
}
export function productionReport(v: WorkspaceView, kind: PeriodKind, today: Day, filter?: (e: ProductionEvent) => boolean) {
  const cfg = productionSettings(v);
  const { current, previous } = comparableRange(kind, today, cfg);
  const events = v.rows('production_events');
  const cur = totalsOf(events, current, filter);
  const prev = totalsOf(events, previous, filter);
  const currencies = [...new Set([...Object.keys(cur.byCurrency), ...Object.keys(prev.byCurrency)])];
  const change = Object.fromEntries(currencies.map((c) => [c, changePct(cur.byCurrency[c] ?? 0, prev.byCurrency[c])]));
  return { kind, current, previous, cur, prev, change };
}
/** Reconciliation: ledger total must equal the sum of its drill-down rows. */
export function reconcile(t: ProductionTotals): boolean {
  const sums: Record<string, number> = {};
  for (const e of t.events) add(sums, e.currency, e.annualDelta);
  return Object.keys({ ...sums, ...t.byCurrency }).every((c) => (sums[c] ?? 0) === (t.byCurrency[c] ?? 0));
}
export function assertNoDoubleCredit(v: WorkspaceView): void {
  const seen = new Set<string>();
  for (const e of v.rows('production_events')) { if (seen.has(e.key)) throw new DomainError('production.duplicate_key', e.key); seen.add(e.key); }
}
